import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaFilter,
  FaSignOutAlt,
  FaTimes,
  FaTrash,
  FaUser,
  FaUsers,
} from "react-icons/fa";

import "./AdminDashboard.css";
import EntriesFilter from "./EntriesFilter";
import EntriesTable from "./EntriesTable";
import UserList from "./UserList";
import UserForm from "./UserForm";
import { API_URL } from "../../config";

const AdminDashboard = ({ onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [activeSection, setActiveSection] = useState("verwaltung");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);

  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const currentRole = localStorage.getItem("role") || "user";
  const currentEmail = localStorage.getItem("email") || "";

  const showStatus = useCallback((type, text) => {
    setMessage({ type, text });

    window.setTimeout(() => {
      setMessage(null);
    }, 4000);
  }, []);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [entriesResponse, usersResponse] = await Promise.all([
        fetch(`${API_URL}/api/entries`),
        fetch(`${API_URL}/api/users`),
      ]);

      const entriesData = await entriesResponse.json().catch(() => []);
      const usersData = await usersResponse.json().catch(() => []);

      if (!entriesResponse.ok) {
        throw new Error(
          entriesData?.error || "Einträge konnten nicht geladen werden."
        );
      }

      if (!usersResponse.ok) {
        throw new Error(
          usersData?.error || "Benutzer konnten nicht geladen werden."
        );
      }

      setEntries(Array.isArray(entriesData) ? entriesData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (loadError) {
      console.error("Dashboard konnte nicht geladen werden:", loadError);
      setError(loadError.message || "Dashboard konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const fetchFilteredEntries = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (selectedUser) {
        params.append("user", selectedUser);
      }

      if (startDate) {
        params.append("start", `${startDate} 00:00:00`);
      }

      if (endDate) {
        params.append("end", `${endDate} 23:59:59`);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL}/api/entries?${queryString}`
        : `${API_URL}/api/entries`;

      const response = await fetch(url);
      const data = await response.json().catch(() => []);

      if (!response.ok) {
        throw new Error(data?.error || "Filter konnte nicht angewendet werden.");
      }

      setEntries(Array.isArray(data) ? data : []);
      setSelectedEntries([]);
      setIsFilterModalOpen(false);
      showStatus("success", "Filter wurde angewendet.");
    } catch (filterError) {
      console.error("Filterfehler:", filterError);
      setError(filterError.message || "Filter konnte nicht angewendet werden.");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = async () => {
    setSelectedUser("");
    setStartDate("");
    setEndDate("");
    setIsFilterModalOpen(false);
    setSelectedEntries([]);
    await loadDashboardData();
  };

  const handleDeleteSelected = async () => {
    if (selectedEntries.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Möchtest du ${selectedEntries.length} Einträge wirklich löschen?`
    );

    if (!confirmed) {
      return;
    }

    setDeleting(true);

    try {
      const response = await fetch(`${API_URL}/api/entries`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ids: selectedEntries,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || "Die Einträge konnten nicht gelöscht werden."
        );
      }

      setEntries((previousEntries) =>
        previousEntries.filter(
          (entry) => !selectedEntries.includes(entry.id)
        )
      );

      const deletedCount = selectedEntries.length;
      setSelectedEntries([]);

      showStatus(
        "success",
        `${deletedCount} Einträge wurden erfolgreich gelöscht.`
      );
    } catch (deleteError) {
      console.error("Fehler beim Löschen:", deleteError);

      showStatus(
        "error",
        deleteError.message || "Einträge konnten nicht gelöscht werden."
      );
    } finally {
      setDeleting(false);
    }
  };

  const statistics = useMemo(() => {
    const today = new Date();
    const todayKey = today.toISOString().slice(0, 10);

    const todaysEntries = entries.filter((entry) => {
      const entryDate = new Date(entry.time);

      if (Number.isNaN(entryDate.getTime())) {
        return false;
      }

      return entryDate.toISOString().slice(0, 10) === todayKey;
    }).length;

    const activeUsers = new Set(
      entries
        .map((entry) => entry.user_id)
        .filter((userId) => userId !== null && userId !== undefined)
    ).size;

    return {
      entries: entries.length,
      users: users.length,
      todaysEntries,
      activeUsers,
    };
  }, [entries, users]);

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-brand">
          <img
            src={`${process.env.PUBLIC_URL}/Images/qroof-logo.png`}
            alt="QRoof"
            className="admin-logo"
          />

          <div>
            <p className="admin-eyebrow">QRoof</p>
            <h1>Administration</h1>
          </div>
        </div>

        <div className="admin-header-actions">
          <div className="current-user">
            <div className="current-user-icon">
              <FaUser />
            </div>

            <div>
              <strong>{currentEmail || "Administrator"}</strong>
              <span>{currentRole}</span>
            </div>
          </div>

          <button
            type="button"
            className="admin-logout-button"
            onClick={onLogout}
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main className="admin-content">
        {message && (
          <div
            className={`admin-status admin-status-${message.type}`}
            role="status"
          >
            {message.type === "success" ? (
              <FaCheckCircle />
            ) : (
              <FaExclamationTriangle />
            )}

            <span>{message.text}</span>

            <button
              type="button"
              aria-label="Meldung schließen"
              onClick={() => setMessage(null)}
            >
              <FaTimes />
            </button>
          </div>
        )}

        <section className="admin-intro">
          <p className="section-label">Übersicht</p>
          <h2>Verwaltung und Benutzer</h2>
          <p>
            Einträge filtern, Arbeitsdaten verwalten und Benutzerkonten
            organisieren.
          </p>
        </section>

        <section className="statistics-grid">
          <article className="stat-card">
            <div className="stat-icon">
              <FaClipboardList />
            </div>

            <div>
              <span>Einträge gesamt</span>
              <strong>{statistics.entries}</strong>
            </div>
          </article>

          <article className="stat-card">
            <div className="stat-icon">
              <FaUsers />
            </div>

            <div>
              <span>Benutzer</span>
              <strong>{statistics.users}</strong>
            </div>
          </article>

          <article className="stat-card">
            <div className="stat-icon">
              <FaCheckCircle />
            </div>

            <div>
              <span>Heute erfasst</span>
              <strong>{statistics.todaysEntries}</strong>
            </div>
          </article>

          <article className="stat-card">
            <div className="stat-icon">
              <FaUser />
            </div>

            <div>
              <span>Aktive Benutzer</span>
              <strong>{statistics.activeUsers}</strong>
            </div>
          </article>
        </section>

        <nav className="admin-tabs" aria-label="Administrationsbereiche">
          <button
            type="button"
            className={activeSection === "verwaltung" ? "active" : ""}
            onClick={() => setActiveSection("verwaltung")}
          >
            <FaClipboardList />
            Verwaltung
          </button>

          <button
            type="button"
            className={activeSection === "benutzer" ? "active" : ""}
            onClick={() => setActiveSection("benutzer")}
          >
            <FaUsers />
            Benutzer
          </button>
        </nav>

        {activeSection === "verwaltung" && (
          <section className="admin-panel">
            <div className="panel-heading">
              <div>
                <p className="section-label">Arbeitsdaten</p>
                <h2>Erfasste Einträge</h2>
              </div>

              <span className="panel-count">{entries.length}</span>
            </div>

            <div className="admin-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={() => setIsFilterModalOpen(true)}
              >
                <FaFilter />
                Filter
              </button>

              <button
                type="button"
                className="danger-action"
                disabled={selectedEntries.length === 0 || deleting}
                onClick={handleDeleteSelected}
              >
                <FaTrash />
                {deleting
                  ? "Wird gelöscht ..."
                  : `${selectedEntries.length || ""} Löschen`}
              </button>
            </div>

            {loading && (
              <div className="admin-loading">
                <div className="admin-spinner" />
                <p>Daten werden geladen ...</p>
              </div>
            )}

            {!loading && error && (
              <div className="admin-error-state">
                <FaExclamationTriangle />
                <h3>Daten konnten nicht geladen werden</h3>
                <p>{error}</p>

                <button type="button" onClick={loadDashboardData}>
                  Erneut versuchen
                </button>
              </div>
            )}

            {!loading && !error && entries.length === 0 && (
              <div className="admin-empty-state">
                <FaClipboardList />
                <h3>Keine Einträge vorhanden</h3>
                <p>
                  Sobald ein QR-Code gescannt wurde, erscheint der Eintrag
                  hier.
                </p>
              </div>
            )}

            {!loading && !error && entries.length > 0 && (
              <div className="entries-table-wrapper">
                <EntriesTable
                  entries={entries}
                  selectedEntries={selectedEntries}
                  setSelectedEntries={setSelectedEntries}
                />
              </div>
            )}
          </section>
        )}

        {activeSection === "benutzer" && (
          <section className="admin-panel">
            <div className="panel-heading">
              <div>
                <p className="section-label">Konten</p>
                <h2>Benutzerverwaltung</h2>
              </div>

              <span className="panel-count">{users.length}</span>
            </div>

            <div className="user-management-grid">
              <div className="user-list-card">
                <UserList users={users} />
              </div>

              {currentRole === "admin" ||
              currentRole === "superadmin" ? (
                <div className="user-form-card">
                  <UserForm
                    users={users}
                    setUsers={setUsers}
                    currentRole={currentRole}
                  />
                </div>
              ) : (
                <div className="admin-no-permission">
                  <FaExclamationTriangle />
                  <h3>Keine Berechtigung</h3>
                  <p>
                    Dein Benutzerkonto darf keine neuen Benutzer anlegen.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {isFilterModalOpen && (
        <div className="admin-modal-overlay">
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-title"
          >
            <div className="admin-modal-header">
              <div>
                <p className="section-label">Auswahl eingrenzen</p>
                <h2 id="filter-title">Einträge filtern</h2>
              </div>

              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setIsFilterModalOpen(false)}
                aria-label="Filter schließen"
              >
                <FaTimes />
              </button>
            </div>

            <EntriesFilter
              users={users}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />

            <div className="filter-footer">
              <button
                type="button"
                className="filter-reset-button"
                onClick={resetFilters}
              >
                Filter zurücksetzen
              </button>

              <button
                type="button"
                className="filter-apply-button"
                onClick={fetchFilteredEntries}
              >
                <FaFilter />
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;